import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api-client';
import { toast } from '@/components/ui/toast';
import { errorMessage as extractErrorMessage } from '@/lib/errors';

interface Options {
  /**
   * Catalog key naming the resource, e.g. `common.resources.member`. The toast
   * sentence itself is `common.crud.*`, so this is the only part a page has to
   * name. Prefer it over `label`, which cannot be translated.
   */
  labelKey?: string;
  /**
   * Literal label, for a resource with no catalog entry. Wins over `labelKey`
   * when both are given, and reaches the operator untranslated — which is what
   * every call site did before the sentences moved into the catalog.
   */
  label?: string;
  /** Field used as the record id in update/remove calls. Defaults to 'id'. */
  idField?: string;
  /** Show success/error toasts. Defaults to true. */
  toast?: boolean;
}

/**
 * Standard create / update / remove mutations for a RESTful resource:
 *   POST    /{resource}       → create
 *   PATCH   /{resource}/:id   → update
 *   DELETE  /{resource}/:id   → remove
 *
 * Each mutation invalidates the resource's list queries
 * (`queryKey: [resource]`) on success and surfaces a toast.
 */
export function useCrudMutations<TRow, TCreate = Partial<TRow>, TUpdate = Partial<TRow>>(
  resource: string,
  opts: Options = {},
) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const showToast = opts.toast !== false;
  const name =
    opts.label ??
    (opts.labelKey ? t(opts.labelKey) : resource.replace(/^.*\//, '').replace(/-/g, ' '));
  const idField = opts.idField ?? 'id';

  const invalidate = () => qc.invalidateQueries({ queryKey: [resource] });

  const create = useMutation<TRow, unknown, TCreate>({
    mutationFn: (body) => api.post<TRow, TCreate>(resource, body),
    onSuccess: () => {
      invalidate();
      if (showToast) toast.success(t('common.crud.created', { name }));
    },
    onError: (err) => {
      if (showToast) toast.error(extractErrorMessage(err));
    },
  });

  const update = useMutation<TRow, unknown, { id: string | number; data: TUpdate }>({
    mutationFn: ({ id, data }) => api.patch<TRow, TUpdate>(`${resource}/${id}`, data),
    onSuccess: () => {
      invalidate();
      if (showToast) toast.success(t('common.crud.saved', { name }));
    },
    onError: (err) => {
      if (showToast) toast.error(extractErrorMessage(err));
    },
  });

  const remove = useMutation<void, unknown, string | number | Record<string, unknown>>({
    mutationFn: (idOrRow) => {
      const id =
        typeof idOrRow === 'object' && idOrRow !== null
          ? (idOrRow as Record<string, unknown>)[idField]
          : idOrRow;
      return api.delete<void>(`${resource}/${id}`);
    },
    onSuccess: () => {
      invalidate();
      if (showToast) toast.success(t('common.crud.deleted', { name }));
    },
    onError: (err) => {
      if (showToast) toast.error(extractErrorMessage(err));
    },
  });

  return { create, update, remove };
}
